import React, { useState, useEffect } from 'react'; // Added useState, useEffect
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, CheckCircle, LinkIcon } from 'lucide-react'; // Added LinkIcon
import { usePmt3ProblemsDashboardData } from '@/hooks/usePmt3ProblemsDashboardData';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

/**
 * Transforms a base Google Sheet URL by removing query parameters and appending a GID fragment.
 * @param baseUrl The original URL string from Firestore.
 * @param gid The Google Sheet GID to append.
 * @returns The transformed URL or null if the base URL is invalid.
 */
const transformSheetUrl = (baseUrl: string | undefined | null, gid: string): string | null => {
    if (!baseUrl || typeof baseUrl !== 'string') {
        return null;
    }
    try {
        const url = new URL(baseUrl);
        // Remove existing search parameters like ?usp=drivesdk
        url.search = '';
        // Append the GID fragment
        url.hash = `gid=${gid}`;
        return url.toString();
    } catch (error) {
        console.error("Error transforming sheet URL:", baseUrl, error);
        // Fallback: try simple string manipulation if URL parsing fails
        const base = baseUrl.split('?')[0];
        if (base) {
            return `${base}#gid=${gid}`;
        }
        return null;
    }
};

// Reusable fetchClientUrl function (copied for now, consider moving to shared util)
const fetchClientUrl = async (companyName: string, docId: string): Promise<string | null> => {
    if (!companyName || !docId) return null;
    try {
        // Sanitize docId before using it in the path - crucial for keys with spaces/periods
        const sanitizedDocId = docId.replace(/\//g, '_').replace(/\./g, '_');
        const urlDocRef = doc(db, 'pmt2_clienturldatabase', companyName, 'items', sanitizedDocId);
        const urlDocSnap = await getDoc(urlDocRef);
        return urlDocSnap.exists() ? urlDocSnap.data()?.value ?? null : null;
    } catch (error) {
        console.error(`Error fetching client URL for ${docId}:`, error);
        return null;
    }
};


interface DashboardItemProps {
    item: string;
    value: React.ReactNode;
    isAlert?: boolean; // Optional flag for styling
    isLoading?: boolean;
    colorClass?: string; // Optional specific color class
    linkUrl?: string | null; // Optional URL for the item label
}

const DashboardItem: React.FC<DashboardItemProps> = ({ item, value, isAlert = false, isLoading = false, colorClass, linkUrl }) => {
    // Determine color: Use specific colorClass if provided, otherwise use alert/default logic
    const calculatedColor = colorClass
        ? colorClass
        : isAlert
        ? 'text-red-600'
        : value === 'No'
        ? 'text-green-700' // Use darker green for 'No'
        : ''; // Default text color

    // Determine icon based on alert or 'No' value, ignore for specific colorClass items unless explicitly needed
    const Icon = !colorClass && isAlert ? AlertCircle : (!colorClass && value === 'No' ? CheckCircle : null);

    const itemContent = linkUrl ? (
        <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            {item}
        </a>
    ) : (
        item
    );

    return (
        <TableRow>
            <TableCell className="font-medium py-2 px-4">{itemContent}</TableCell> {/* Render item or link */}
            <TableCell className={`text-right py-2 px-4 ${calculatedColor}`}> {/* Added padding */}
                {isLoading ? (
                    <Skeleton className="h-4 w-16 float-right" />
                ) : (
                    <div className="flex items-center justify-end space-x-1">
                        {/* Render icon only if it's defined */}
                        {Icon && <Icon size={16} />}
                        <span>{value}</span>
                    </div>
                )}
            </TableCell>
        </TableRow>
    );
};


const ProblemsDashboard: React.FC = () => {
    const { selectedCompany, selectedProduct } = usePMT3Selection();
    const { data, isLoading, error } = usePmt3ProblemsDashboardData(selectedCompany);
    const [potentialProblemsUrl, setPotentialProblemsUrl] = useState<string | null>(null); // State for URL
    const [oldValuesMap, setOldValuesMap] = useState<Record<string, string> | null>(null);

    const currentAsin = selectedProduct?.id;

    // Fetch Potential Problems URL when company changes
    useEffect(() => {
        const fetchUrl = async () => {
            if (selectedCompany) {
                const url = await fetchClientUrl(selectedCompany, 'Potential Problems');
                setPotentialProblemsUrl(url);
            } else {
                setPotentialProblemsUrl(null); // Reset if no company selected
            }
        };
        fetchUrl();
    }, [selectedCompany]);

    // Fetch old values for impression decline comparison
    useEffect(() => {
        setOldValuesMap(null);
        if (selectedCompany && currentAsin) {
            fetchLatestOldValuesMap(selectedCompany, currentAsin)
                .then(setOldValuesMap)
                .catch(() => setOldValuesMap(null));
        }
    }, [selectedCompany, currentAsin]);


    // --- Data Processing Logic (ASIN Specific) ---

    // Find data specific to the selected ASIN
    const currentProductData = data?.productData?.find(p => p.id === currentAsin);
    const currentAvgInventory = data?.avgInventoryLevels?.find(entry => entry.id === currentAsin);
    const currentCaMicroIssue = data?.caMicroAutoIssues?.find(entry => entry.id === currentAsin); // Assuming one entry per ASIN if issues exist
    const currentProfitDecline = data?.profitDeclineSkus?.find(entry => entry.id === currentAsin);

    // --- Calculate URLs ---
    const budgetCheckerBaseUrl = data?.budgetCheckerUrl;
    const caAutoBidAdjusterBaseUrl = data?.caAutoBidAdjusterUrl;
    const salesProfitHistoryBaseUrl = data?.salesProfitHistoryUrl;

    const budgetCheckerUrl = transformSheetUrl(budgetCheckerBaseUrl, '187118097');
    const campaignsNotShowingUrl = transformSheetUrl(budgetCheckerBaseUrl, '1140076585');
    const caMicroBidsUrl = transformSheetUrl(caAutoBidAdjusterBaseUrl, '133688401');
    // const caMicroAcosUrl = caMicroBidsUrl; // Same URL as bids
    const profitDecline3mUrl = transformSheetUrl(salesProfitHistoryBaseUrl, '1272578415');


    // Campaigns Running Out of Budget
    const campaignsOutOfBudgetValueRaw = currentProductData?.['Campaigns Running Out of Budget'];
    const campaignsOutOfBudgetValue = campaignsOutOfBudgetValueRaw && campaignsOutOfBudgetValueRaw !== '0' ? campaignsOutOfBudgetValueRaw : '-';
    const campaignsOutOfBudgetAlert = campaignsOutOfBudgetValue !== '-';

    // Campaigns Not Showing at All
    const campaignsNotShowingValueRaw = currentProductData?.['Campaigns Not Showing at All'];
    const campaignsNotShowingValue = campaignsNotShowingValueRaw && campaignsNotShowingValueRaw !== '0' ? campaignsNotShowingValueRaw : '-';
    const campaignsNotShowingAlert = campaignsNotShowingValue !== '-';

    // Possibly Raise CA/Micro Auto Bids
    const raiseBids = currentCaMicroIssue?.issue_type === 'Low Clicks';
    const raiseBidsValue = raiseBids ? 'Yes' : 'No';
    const raiseBidsAlert = raiseBids;

    // CA/Micro Auto Bad ACoS
    const badAcos = currentCaMicroIssue?.issue_type === 'Bad ACoS';
    const badAcosValue = badAcos ? 'Yes' : 'No';
    const badAcosAlert = badAcos;

    // Avg 28 Day Buy Box %
    const buyBoxPercentRaw = currentAvgInventory?.average_28_day_buy_box_percentage;
    const buyBoxPercent = typeof buyBoxPercentRaw === 'number' ? buyBoxPercentRaw : parseFloat(buyBoxPercentRaw);
    const buyBoxPercentNumeric = !isNaN(buyBoxPercent) ? Math.round(buyBoxPercent * 100) : 0;
    const avgBuyBoxPercentValue = `${buyBoxPercentNumeric}%`;
    // Determine color based on numeric value using the new 6-level scale
    let avgBuyBoxPercentColor = 'text-red-600'; // Default: 0%
    if (buyBoxPercentNumeric >= 98) {
        avgBuyBoxPercentColor = 'text-green-700'; // 98-100%
    } else if (buyBoxPercentNumeric >= 90) {
        avgBuyBoxPercentColor = 'text-green-600'; // 90-97%
    } else if (buyBoxPercentNumeric >= 75) {
        avgBuyBoxPercentColor = 'text-lime-600'; // 75-89%
    } else if (buyBoxPercentNumeric >= 50) {
        avgBuyBoxPercentColor = 'text-yellow-500'; // 50-74%
    } else if (buyBoxPercentNumeric > 0) {
        avgBuyBoxPercentColor = 'text-orange-500'; // 1-49%
    }

    // ASINs with No CTA Targets
    const noCtaTargets = !!currentAsin && (data?.problemsData?.noCtaTargets?.includes(currentAsin) ?? false);
    const noCtaTargetsValue = noCtaTargets ? 'Yes' : 'No';
    const noCtaTargetsAlert = noCtaTargets;

    // ASINs with No Broad Seeds
    const noBroadSeeds = !!currentAsin && (data?.problemsData?.noBroadSeeds?.includes(currentAsin) ?? false);
    const noBroadSeedsValue = noBroadSeeds ? 'Yes' : 'No';
    const noBroadSeedsAlert = noBroadSeeds;

    // Never Farmed ASINs
    const neverFarmed = !!currentAsin && (data?.problemsData?.neverFarmed?.includes(currentAsin) ?? false);
    const neverFarmedValue = neverFarmed ? 'Yes' : 'No';
    const neverFarmedAlert = neverFarmed;

    // ASINs With Insufficient Farming Time
    const insufficientFarmingTime = !!currentAsin && (data?.problemsData?.insufficientFarmingTime?.includes(currentAsin) ?? false);
    const insufficientFarmingTimeValue = insufficientFarmingTime ? 'Yes' : 'No';
    const insufficientFarmingTimeAlert = insufficientFarmingTime;

    // Ignore from Farming Problem Alerts Until
    const ignoreUntilDate = currentProductData?.['Ignore from Farming Problem Alerts Until'] || '-';
    const ignoreUntilDateAlert = false; // No alert status defined

    // Profit Decline vs 3 Months Ago
    const decline3Months = !!currentProfitDecline?.substantial_gross_profit_decline_vs_3_months_ago;
    const decline3MonthsValue = decline3Months ? 'Yes' : 'No';
    const decline3MonthsAlert = decline3Months;

    // Profit Decline vs 12 Months Ago
    const decline12Months = !!currentProfitDecline?.substantial_gross_profit_decline_vs_last_year;
    const decline12MonthsValue = decline12Months ? 'Yes' : 'No';
    const decline12MonthsAlert = decline12Months;

    // Impression Decline vs Last Review
    const currentImpRaw = currentProductData?.['28 Day PPC Imp'];
    const oldImpRaw = oldValuesMap?.['28 Day PPC Imp'];
    const currentImp = typeof currentImpRaw === 'number' ? currentImpRaw : parseFloat(String(currentImpRaw ?? ''));
    const oldImp = parseFloat(String(oldImpRaw ?? ''));
    const hasImpressionData = !isNaN(currentImp) && !isNaN(oldImp) && oldImp > 0;
    const impressionDeclinePercent = hasImpressionData ? Math.round(((currentImp - oldImp) / oldImp) * 100) : 0;
    const impressionDecline = hasImpressionData && impressionDeclinePercent < -15;
    const impressionDeclineValue = !hasImpressionData ? '-' : impressionDecline ? `${impressionDeclinePercent}%` : 'No';
    const impressionDeclineAlert = impressionDecline;


    if (error) {
        return <Card><CardHeader><CardTitle>Problems</CardTitle></CardHeader><CardContent><p className="text-red-600">Error loading dashboard data: {error.message}</p></CardContent></Card>;
    }

    // Split items for two columns - REORDERED
    const column1Items = [
        { item: "Campaigns Running Out of Budget", value: campaignsOutOfBudgetValue, isAlert: campaignsOutOfBudgetAlert, linkUrl: budgetCheckerUrl },
        { item: "Campaigns Not Showing at All", value: campaignsNotShowingValue, isAlert: campaignsNotShowingAlert, linkUrl: campaignsNotShowingUrl },
        { item: "Possibly Raise CA/Micro Auto Bids", value: raiseBidsValue, isAlert: raiseBidsAlert, linkUrl: caMicroBidsUrl },
        { item: "CA/Micro Auto Bad ACoS", value: badAcosValue, isAlert: badAcosAlert, linkUrl: caMicroBidsUrl }, // Use same URL as bids
        { item: "Avg 28 Day Buy Box %", value: avgBuyBoxPercentValue, colorClass: avgBuyBoxPercentColor },
        { item: "ASINs with No CTA Targets", value: noCtaTargetsValue, isAlert: noCtaTargetsAlert },
    ];

    const column2Items = [
        { item: "ASINs with No Broad Seeds", value: noBroadSeedsValue, isAlert: noBroadSeedsAlert },
        { item: "Never Farmed ASINs", value: neverFarmedValue, isAlert: neverFarmedAlert },
        { item: "ASINs With Insufficient Farming Time", value: insufficientFarmingTimeValue, isAlert: insufficientFarmingTimeAlert },
        { item: "Ignore from Farming Problem Alerts Until", value: ignoreUntilDate, isAlert: ignoreUntilDateAlert },
        { item: "Profit Decline vs 3 Months Ago", value: decline3MonthsValue, isAlert: decline3MonthsAlert, linkUrl: profitDecline3mUrl },
        { item: "Profit Decline vs 12 Months Ago", value: decline12MonthsValue, isAlert: decline12MonthsAlert },
        { item: "Impression Decline vs Last Review", value: impressionDeclineValue, isAlert: impressionDeclineAlert },
    ];

    return (
        <Card className="max-w-4xl mx-auto">
            <CardHeader className="p-4">
                 {/* Wrap Title and Icon Link in a flex container */}
                <div className="flex items-center gap-2">
                    <CardTitle className="text-2xl font-bold">Problems</CardTitle> {/* Changed Title */}
                    {potentialProblemsUrl && (
                         <a
                            href={potentialProblemsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Potential Problems Sheet"
                            className="text-blue-500 hover:text-blue-700 inline-flex items-center justify-center"
                         >
                            <LinkIcon size={18} />
                         </a>
                    )}
                </div>
                {!selectedProduct && !isLoading && <p className="text-sm text-gray-500 mt-1">Select a product to view specific problems.</p>}
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                    {/* Column 1 */}
                    <Table>
                        <TableBody>
                            {column1Items.map((itemData) => (
                                <DashboardItem key={itemData.item} {...itemData} isLoading={isLoading} />
                            ))}
                        </TableBody>
                    </Table>
                    {/* Column 2 */}
                    <Table>
                        <TableBody>
                            {column2Items.map((itemData) => (
                                <DashboardItem key={itemData.item} {...itemData} isLoading={isLoading} />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default ProblemsDashboard;